import React, { useEffect, useMemo, useState } from "react";

import URL from "../../config/api";

import "./relatorio.css";

/* Peças compartilhadas pelos relatórios de quiz e de atividade.

   As duas telas respondem à mesma pergunta — quem fez e como foi — para o mesmo
   professor, então o indicador, a barra, a pílula de situação e a paginação
   moram aqui. Quando cada uma tinha a sua cópia, o ajuste entrava em um lado e
   esquecia o outro. */

export const POR_PAGINA = 10;

export const formatarData = valor => {
  if (!valor) return "—";
  /* O backend manda "Y-m-d H:i:s". O Safari não parseia essa string com espaço,
     então o T entra à mão em vez de confiar no construtor do Date. */
  const data = new Date(String(valor).replace(" ", "T"));
  if (Number.isNaN(data.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(data);
};

export const Avatar = ({ foto }) => (
  <img
    className="stat-report__avatar"
    src={URL.img + (foto || "uploads/users/profileEmpty.jpg")}
    alt=""
  />
);

export const Indicadores = ({ itens }) => (
  <section className="stat-report__stats">
    {itens.map(item => (
      <div className="stat-report__stat" key={item.label}>
        <span className="stat-report__stat-label">{item.label}</span>
        <div className="stat-report__stat-value">{item.valor}</div>
        <div className="stat-report__stat-hint">{item.hint}</div>
      </div>
    ))}
  </section>
);

export const Filtros = ({ opcoes, atual, onTrocar }) => (
  <div className="stat-report__filters">
    {opcoes.map(opcao => (
      <button
        type="button"
        key={opcao.id}
        className={`stat-report__filter${
          atual === opcao.id ? " stat-report__filter--ativo" : ""
        }`}
        onClick={() => onTrocar(opcao.id)}
      >
        {opcao.rotulo}
      </button>
    ))}
  </div>
);

const faixaDaBarra = percentual =>
  percentual >= 70
    ? ""
    : percentual >= 40
    ? " stat-report__bar--medio"
    : " stat-report__bar--baixo";

/* Percentual com barra. O número aparece sempre junto dela porque cor não pode
   ser o único indicador — vale para quem não distingue verde de vermelho e para
   quem imprime a tela em preto e branco. */
export const Medidor = ({ percentual }) => {
  const vazio = percentual === null || percentual === undefined;
  const valor = vazio ? 0 : Number(percentual);

  return (
    <div className="stat-report__rate">
      <span className="stat-report__rate-value">
        {vazio ? "—" : `${valor.toFixed(0)}%`}
      </span>
      <div className={`stat-report__bar${faixaDaBarra(valor)}`}>
        <span style={{ width: `${vazio ? 0 : valor}%` }} />
      </div>
    </div>
  );
};

export const Pilula = ({ estado, children }) => (
  <span
    className={`stat-report__pill${
      estado ? ` stat-report__pill--${estado}` : ""
    }`}
  >
    {children}
  </span>
);

export const Vazio = ({ titulo, texto }) => (
  <div className="stat-report__empty">
    <strong>{titulo}</strong>
    <span>{texto}</span>
  </div>
);

/* Janela de páginas: com muitas páginas, mostra as vizinhas da atual em vez de
   uma fileira de números que não cabe. */
const paginasVisiveis = (atual, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const inicio = Math.max(1, Math.min(atual - 2, total - 4));
  const fim = Math.min(total, inicio + 4);
  const paginas = [];
  if (inicio > 1) paginas.push(1, "...");
  for (let i = inicio; i <= fim; i += 1) paginas.push(i);
  if (fim < total) paginas.push("...", total);
  return paginas;
};

export const Paginacao = ({
  pagina,
  totalPaginas,
  total,
  primeiro,
  ultimo,
  onIr
}) => (
  <div className="stat-report__pagination">
    <span className="stat-report__pagination-info">
      {`Mostrando ${primeiro}-${ultimo} de ${total} estudante(s)`}
    </span>
    {totalPaginas > 1 && (
      <div className="stat-report__pages">
        <button
          type="button"
          className="stat-report__page"
          disabled={pagina === 1}
          onClick={() => onIr(pagina - 1)}
        >
          Anterior
        </button>
        {paginasVisiveis(pagina, totalPaginas).map((numero, indice) =>
          numero === "..." ? (
            <span
              className="stat-report__pages-gap"
              key={`gap-${indice}`}
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <button
              type="button"
              key={numero}
              className={`stat-report__page${
                numero === pagina ? " stat-report__page--ativa" : ""
              }`}
              onClick={() => onIr(numero)}
            >
              {numero}
            </button>
          )
        )}
        <button
          type="button"
          className="stat-report__page"
          disabled={pagina === totalPaginas}
          onClick={() => onIr(pagina + 1)}
        >
          Próxima
        </button>
      </div>
    )}
  </div>
);

/* Paginação de uma lista já filtrada. `itens` precisa ser estável entre
   renders (um useMemo), senão o efeito que volta para a página 1 dispara a cada
   render. */
export const usePaginacao = itens => {
  const [pagina, setPagina] = useState(1);
  const totalPaginas = Math.max(1, Math.ceil(itens.length / POR_PAGINA));

  /* Trocar de filtro (ou receber outra turma) com a página 3 na tela deixaria o
     professor olhando uma lista vazia. */
  useEffect(() => setPagina(1), [itens]);
  useEffect(
    () => setPagina(atual => Math.min(atual, totalPaginas)),
    [totalPaginas]
  );

  const naPagina = useMemo(
    () => itens.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA),
    [itens, pagina]
  );

  return {
    pagina,
    irPara: setPagina,
    totalPaginas,
    naPagina,
    primeiro: itens.length ? (pagina - 1) * POR_PAGINA + 1 : 0,
    ultimo: Math.min(pagina * POR_PAGINA, itens.length)
  };
};
